import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { fetchCmsList } from '../lib/cms';
import { GalleryLightbox } from '../components/GalleryLightbox';

export function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [activeGalleryKey, setActiveGalleryKey] = useState('all');

  useEffect(() => {
    async function load() {
      const data = await fetchCmsList('gallery');
      if (data.length) setGalleries(data);
    }
    load();
  }, []);

  const allImages = galleries.flatMap((g) => g.images || []);
  const activeGallery = galleries.find((g) => g.gallery_key === activeGalleryKey || g.slug?.da === activeGalleryKey);

  return (
    <>
      <SEO 
        title="Billedgalleri | Fodtid"
        description="Se billeder fra vores udkørende fodbehandlinger i hjemmet, udstyr, naturlige urtecremer og resultater hos Fodtid."
      />

      {/* Header */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center space-y-3">
          <span className="text-xs uppercase font-bold tracking-widest text-brand-charcoal bg-surface px-3.5 py-1 rounded-full border border-brand-border">
            Fodtid i Billeder
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal">
            Billedgalleri
          </h1>
          <p className="text-brand-muted text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Få et indblik i vores udkørende arbejde, professionelle udstyr, naturlige produkter og sunde resultater. Klik på et billede for fuld skærmvisning.
          </p>
        </div>
      </section>

      {/* Category selector */}
      <section className="py-6 bg-surface/40 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-center gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setActiveGalleryKey('all')}
            className={`px-5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all focus:outline-none ${
              activeGalleryKey === 'all'
                ? 'bg-brand-charcoal text-canvas shadow-sm'
                : 'bg-surface text-brand-charcoal hover:bg-surface-soft border border-brand-border'
            }`}
          >
            Alle Billeder ({allImages.length})
          </button>
          
          {galleries.map((g) => (
            <button
              key={g.id || g.gallery_key}
              type="button"
              onClick={() => setActiveGalleryKey(g.gallery_key)}
              className={`px-5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all focus:outline-none ${
                activeGalleryKey === g.gallery_key
                  ? 'bg-brand-charcoal text-canvas shadow-sm'
                  : 'bg-surface text-brand-charcoal hover:bg-surface-soft border border-brand-border'
              }`}
            >
              {g.title} ({g.images?.length || 0})
            </button>
          ))}
        </div>
      </section>

      {/* Gallery content */}
      <section className="py-16 sm:py-24 bg-canvas border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          {activeGalleryKey === 'all' ? (
            <div className="space-y-12">
              <GalleryLightbox images={allImages} />
            </div>
          ) : (
            <div className="space-y-6">
              {activeGallery && (
                <div className="bg-surface/50 p-6 rounded-2xl border border-brand-border shadow-xs mb-6">
                  <h2 className="font-serif text-2xl font-bold text-brand-charcoal mb-1">
                    {activeGallery.title}
                  </h2>
                  {activeGallery.description && (
                    <p className="text-xs sm:text-sm text-brand-muted">
                      {activeGallery.description}
                    </p>
                  )}
                </div>
              )}
              <GalleryLightbox gallery={activeGallery} />
            </div>
          )}

          {/* CTA at bottom */}
          <div className="mt-16 text-center bg-surface/50 rounded-3xl p-8 sm:p-10 border border-brand-border shadow-xs max-w-2xl mx-auto space-y-4">
            <h3 className="font-serif text-2xl font-bold text-brand-charcoal">
              Klar til professionel omsorg for dine fødder?
            </h3>
            <p className="text-brand-muted text-xs sm:text-sm max-w-md mx-auto">
              Bestil en tid i dag, så kommer vi hjem til dig i trygge rammer.
            </p>
            <div className="pt-2 flex flex-wrap justify-center gap-3">
              <Link
                to="/kontakt"
                className="bg-brand-charcoal hover:bg-black text-canvas font-bold px-7 py-3.5 rounded-full text-xs uppercase tracking-wider shadow transition-colors"
              >
                Bestil tid online
              </Link>
              <Link
                to="/priser"
                className="bg-canvas hover:bg-surface text-brand-charcoal font-semibold px-6 py-3.5 rounded-full text-xs transition-colors border border-brand-border"
              >
                Se priser & abonnement
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
